import { useApp } from '../context/AppContext';
import { SearchInput } from './SearchInput';
import { formatNaira } from '../utils/formatters';

// Icons
const CompareIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
      d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" 
    />
  </svg>
);

const ContrastIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
      d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" 
    />
  </svg>
);

const BookIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} 
      d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" 
    />
  </svg>
);

export function Header() {
  const { state, currentYearSummary, toggleCompareMode, dispatch } = useApp();
  
  const totalAllocation = currentYearSummary?.totalAllocation ?? 0;
  
  return (
    <header className="glass border-b border-surface-700/50 sticky top-0 z-40">
      <div className="container mx-auto px-4 py-3">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          {/* Logo and title */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-accent-400 to-cyan-600 flex items-center justify-center">
                <span className="text-xl font-bold text-white">₦</span>
              </div>
              <div>
                <h1 className="text-xl font-display font-bold text-surface-100">
                  FAAC Viz
                </h1>
                <p className="text-xs text-surface-500">
                  Nigeria State Allocations 2007–2025
                </p>
              </div>
            </div>
            
            {/* Total for year */}
            {currentYearSummary && (
              <div className="hidden md:block border-l border-surface-700 pl-4">
                <p className="text-xs text-surface-500">{state.selectedYear} Total</p>
                <p className="text-lg font-bold text-accent-400 font-mono">
                  {formatNaira(totalAllocation, true)}
                </p>
              </div>
            )}
          </div>
          
          {/* Search and controls */}
          <div className="flex items-center gap-3">
            {/* Search */}
            <div className="w-full lg:w-64">
              <SearchInput />
            </div>
            
            {/* Compare mode toggle */}
            <button
              onClick={toggleCompareMode}
              className={`btn ${
                state.compareMode 
                  ? 'bg-accent-500/20 text-accent-400 border border-accent-500/30' 
                  : 'btn-ghost'
              }`}
              aria-pressed={state.compareMode}
              title="Compare mode"
            >
              <CompareIcon />
              <span className="hidden sm:inline">Compare</span>
            </button>
            
            {/* High contrast toggle */}
            <button
              onClick={() => dispatch({ type: 'TOGGLE_HIGH_CONTRAST' })}
              className={`btn-ghost ${state.highContrastMode ? 'text-accent-400' : ''}`}
              aria-pressed={state.highContrastMode}
              title="Toggle high contrast"
            >
              <ContrastIcon />
            </button>
            
            {/* Story mode link */}
            <a
              href="#story"
              className="btn bg-gradient-to-r from-accent-500 to-cyan-500 text-white hover:from-accent-400 hover:to-cyan-400"
              title="View data story"
            >
              <BookIcon />
              <span className="hidden sm:inline">Story</span>
            </a>
          </div>
        </div>
        
        {/* Compare mode indicator */}
        {state.compareMode && (
          <div className="mt-2 flex items-center gap-2 text-sm">
            <span className="badge-accent">Compare Mode</span>
            <span className="text-surface-400">
              Select up to 3 states to compare • {state.selectedStates.length}/3 selected
            </span>
            {state.selectedStates.length > 0 && (
              <div className="flex items-center gap-1">
                {state.selectedStates.map((stateName, index) => (
                  <span
                    key={stateName}
                    className="px-2 py-0.5 rounded-full text-xs font-medium"
                    style={{ 
                      backgroundColor: `rgba(34, 211, 238, ${0.2 + index * 0.1})`,
                      color: '#22d3ee'
                    }}
                  >
                    {stateName}
                  </span>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
